import React, { useState, useEffect } from 'react';
import { FaSearch, FaUsers, FaComments, FaClock, FaCircle } from 'react-icons/fa';
import { useChat } from '../../context/ChatContext';
import '../../css/Chat.css';

const ChatList = ({ onCreateRoom }) => {
  const { 
    chats, 
    activeChat, 
    selectChat, 
    loadChats, 
    loading, 
    unreadCounts,
    connectionStatus 
  } = useChat();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredChats, setFilteredChats] = useState([]);

  // Chatok betöltése component mount-nál
  useEffect(() => {
    // A ChatContext automatikusan betölti a chat-eket, nincs szükség duplikált hívásra
    // loadChats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keresés és csoportosítás
  useEffect(() => {
    let filtered = chats;

    if (searchTerm.trim()) {
      filtered = chats.filter(chat =>
        chat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (chat.description && chat.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (chat.last_message && chat.last_message.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredChats(filtered);
  }, [chats, searchTerm]);

  // Csoportosítás típus szerint
  const groupedChats = React.useMemo(() => {
    const groups = {
      backend: [],
      client: [],
      group: []
    };

    filteredChats.forEach(chat => {
      if (chat.chat_type === 'group') {
        groups.group.push(chat);
      } else if (chat.contact_type === 'backend') {
        groups.backend.push(chat);
      } else if (chat.contact_type === 'client') {
        groups.client.push(chat);
      }
    });

    return groups;
  }, [filteredChats]);

  // Idő formázás
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('hu-HU', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else if (diffInHours < 168) { // 1 hét
      return date.toLocaleDateString('hu-HU', { 
        weekday: 'short' 
      });
    } else {
      return date.toLocaleDateString('hu-HU', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Chat kiválasztás
  const handleChatSelect = (chat) => {
    selectChat(chat);
  };

  // Keresés reset
  const handleSearchClear = () => {
    setSearchTerm('');
  };

  // Kapcsolati státusz ikon
  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <FaCircle className="connection-status connected" title="Csatlakozva" />;
      case 'connecting':
      case 'reconnecting':
        return <FaCircle className="connection-status connecting" title="Csatlakozás..." />;
      case 'disconnected':
      case 'failed':
        return <FaCircle className="connection-status disconnected" title="Kapcsolat megszakadt" />;
      default:
        return null;
    }
  };

  // Chat item renderelés
  const renderChatItem = (chat) => {
    const isActive = activeChat?.chat_id === chat.chat_id;
    const unreadCount = unreadCounts[chat.chat_id] || 0;

    return (
      <div
        key={chat.chat_id}
        className={`chat-room-list-item ${isActive ? 'active' : ''}`}
        onClick={() => handleChatSelect(chat)}
      >
        <div className="chat-item-main">
          <div className="chat-item-header">
            <div className="chat-item-name">
              <FaUsers className="chat-item-icon" />
              <span className="chat-name">{chat.name}</span>
            </div>

            {chat.last_message_time && (
              <div className="chat-item-time">
                <FaClock className="time-icon" />
                <span>{formatTime(chat.last_message_time)}</span>
              </div>
            )}
          </div>

          {chat.description && (
            <div className="chat-item-description">
              {chat.description}
            </div>
          )}

          {chat.last_message && (
            <div className="chat-item-last-message">
              {chat.last_message.length > 50
                ? `${chat.last_message.substring(0, 50)}...`
                : chat.last_message
              }
            </div>
          )}

          {/* Résztvevők száma */}
          {chat.users && (
            <div className="chat-item-users">
              <FaUsers className="users-icon" />
              <span>{chat.users.length} résztvevő</span>
            </div>
          )}
        </div>

        {/* Olvasatlan üzenetek jelzése */}
        {unreadCount > 0 && (
          <div className="chat-unread-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="chat-sidebar">
      {/* Chat létrehozás gomb */}
      <button className="chat-create-btn" onClick={onCreateRoom}>
        <FaComments />
        Új csoport
      </button>

      {/* Chat lista fejléc */}
      <div className="chat-sidebar-section">
        <div className="chat-sidebar-header">
          <FaComments className="chat-sidebar-icon" />
          <span>Chatek</span>
          {getConnectionStatusIcon()}
        </div>

        {/* Keresés */}
        <div className="chat-search-row">
          <input
            type="text"
            className="chat-search-input"
            placeholder="Keresés chatekben..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="chat-search-btn"
              onClick={handleSearchClear}
              title="Keresés törlése"
            >
              ×
            </button>
          )}
        </div>

        {/* Chat lista */}
        <div className="chat-room-list">
          {loading ? (
            <div className="chat-loading">
              <div className="loading-spinner"></div>
              <span>Chatok betöltése...</span>
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="chat-empty">
              {searchTerm ? (
                <>
                  <FaSearch className="empty-icon" />
                  <span>Nincs találat a keresésre</span>
                </>
              ) : (
                <>
                  <FaComments className="empty-icon" />
                  <span>Még nincsenek chatok</span>
                  <button
                    className="chat-create-link"
                    onClick={onCreateRoom}
                  >
                    Hozz létre egyet!
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Csoportok */}
              {groupedChats.group.length > 0 && (
                <div className="chat-group-section">
                  <div className="chat-group-header">
                    <FaComments className="group-header-icon" />
                    <span>Csoportok</span>
                  </div>
                  {groupedChats.group.map(chat => renderChatItem(chat))}
                </div>
              )}

              {/* Backend adminok */}
              {groupedChats.backend.length > 0 && (
                <div className="chat-group-section">
                  <div className="chat-group-header">
                    <FaUsers className="group-header-icon" />
                    <span>Adminok</span>
                  </div>
                  {groupedChats.backend.map(chat => renderChatItem(chat))}
                </div>
              )}

              {/* Céges kollégák (frontend user-ek) */}
              {groupedChats.client.length > 0 && (
                <div className="chat-group-section">
                  <div className="chat-group-header">
                    <FaUsers className="group-header-icon" />
                    <span>Kollégák</span>
                  </div>
                  {groupedChats.client.map(chat => renderChatItem(chat))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatList;
